import { Tag, Tooltip, Row, Button, Col, Input, AutoComplete, Alert } from 'antd';
import React, { useEffect, useRef } from 'react';
import styles from './styles.module.css';
import { JsonObject, ManualChoice, ManualChoiceItem } from 'services/hitchit/types/Choices';
import jsonpath from 'jsonpath';
import { useState } from 'react';
import {
  createValueForManualChoiceItem,
  createValueFromPath,
} from 'services/hitchit/types/shared/mapChoice';
import { PlusOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import deepEqual from 'components/utils/deepEqual';

function createOptions(jsonObject: JsonObject = {}, jpath = '$..*') {
  const rootOptions = jsonpath
    .nodes(jsonObject, jpath)
    .map((node) => ({ label: node.path, value: node.value }));

  const flatOptions: { label: string; value: string }[] = [];
  const keys = new Set<string>();

  function addToFlatOptions({ label, value }: { label: string; value: string }) {
    if (keys.has(label)) {
      // duplicate
      return;
    }
    keys.add(label);
    const visibleLabel = label.split('$.')[1];

    flatOptions.push({ label: `${visibleLabel} - ${value}`, value: label });
  }

  rootOptions.forEach((option) => {
    try {
      const label = option.label.reduce((prevRes, lb, index) => {
        if (typeof lb === 'number') {
          return `${prevRes}.`;
        }
        if (index === 0) {
          // will always be $ sign
          return `${lb}`;
        } else {
          return `${prevRes}.${lb}`;
        }
      }) as string;

      if (label.indexOf('..') !== -1) {
        addToFlatOptions({ label, value: createValueFromPath(jsonObject, label) });

        return;
      }
      if (label.lastIndexOf('.') === label.length - 1) {
        // ignore array index fields
        return;
      }
      if (typeof option.value === 'string') {
        addToFlatOptions({ label, value: option.value });
      } else if (Array.isArray(option.value)) {
        if (option.value.length === 0) {
          return;
        }

        if (typeof option.value[0] === 'string') {
          const value = option.value.reduce(
            (prevRes, res, index) => `${prevRes} ${index === 0 ? '' : ';'} ${res}`,
            ''
          );
          addToFlatOptions({ label, value });
        }
      }
    } catch (err) {
      return;
    }
  });

  return flatOptions;
}

type CurrentValue = { label: string; value: string };

const EditManualField = ({
  manualChoice,
  currentValue,
  onComplete,
  jsonObject,
  onlyPrefix,
}: {
  manualChoice: ManualChoice;
  onlyPrefix?: boolean;
  jsonObject?: JsonObject;
  currentValue?: CurrentValue;
  onComplete: (value?: string) => void;
}): JSX.Element => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>();
  const [value, setValue] = useState<CurrentValue>();

  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (manualChoice) {
      const generatedOptions = createOptions(onlyPrefix ? {} : jsonObject);
      setOptions(generatedOptions);
    }
  }, [manualChoice, jsonObject, onlyPrefix]);

  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);

  const onChange = (query: string) => {
    // TODO to be correct label should be mapped here, but normally this wont show
    setValue({ value: query, label: query });
  };

  return (
    <AutoComplete
      ref={inputRef}
      style={{ width: 200 }}
      dropdownMatchSelectWidth={500}
      defaultOpen
      // key={value}
      // className={styles.tagInput}
      options={options}
      // options={choice?.choices.map(({ label, id }) => ({ label, value: id }))}
      value={value?.label}
      // onChange={(newValue) =>
      //   onChange(choice?.choices.find(({ id }) => id === newValue))
      // }
      onChange={onChange}
      onSelect={(value) => {
        onComplete(value);
      }}
      onBlur={() => {
        onComplete(value?.value);
      }}
      allowClear
      filterOption={(inputValue, option) => {
        if (!inputValue) {
          return true;
        }
        return (
          !!option?.label &&
          (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
        );
      }}
    >
      <Input
        onPressEnter={() => {
          onComplete(value?.value);
        }}
      />
    </AutoComplete>
  );
};

const ConfigureManualField = ({
  manualChoice,
  preference,
  savedPreference,
  onChange,
  onReset,
  onClear,
  onCancel,
  onSave,
  jsonObject,
}: {
  manualChoice: ManualChoice;
  preference?: ManualChoiceItem[];
  savedPreference?: ManualChoiceItem[];
  onChange: (choices: ManualChoiceItem[]) => void;
  onReset: () => void;
  onClear: () => void;
  onCancel: () => void;
  onSave: () => void;
  jsonObject?: JsonObject;
}): JSX.Element => {
  const currentChoices = preference || [];
  const isRequired = currentChoices.length === 0 && manualChoice?.required;
  const onlyPrefix = manualChoice.onlyPrefix;

  const [editTag, setEditTag] = useState<number | undefined>();

  const handleClose = (index: number) => {
    onComplete(index);
  };

  const handleEdit = (index: number) => {
    setEditTag(index);
  };

  const isEditableTag = (index: number) => editTag !== undefined && index === editTag;

  const onComplete = (index: number, value?: string) => {
    const updatedManualChoiceItem = value
      ? {
          type: value.indexOf('$.') === 0 ? 'mapping' : 'prefix',
          value,
        }
      : undefined;
    setEditTag(undefined);
    if (index === currentChoices.length && updatedManualChoiceItem) {
      if (updatedManualChoiceItem) {
        onChange([...currentChoices, updatedManualChoiceItem]);
      }
    } else {
      if (!updatedManualChoiceItem) {
        onChange(
          currentChoices.filter((_manualChoiceItem: ManualChoiceItem, currentIndex: number) => {
            if (currentIndex === index) {
              return false;
            } else {
              return true;
            }
          })
        );
      } else {
        onChange(
          currentChoices.map((manualChoiceItem: ManualChoiceItem, currentIndex: number) => {
            if (currentIndex === index) {
              return updatedManualChoiceItem;
            } else {
              return manualChoiceItem;
            }
          })
        );
      }
    }
  };
  const choicesTags = currentChoices.map((manualChoiceItem: ManualChoiceItem, index: number) => {
    const tag = createValueForManualChoiceItem(manualChoiceItem, jsonObject);
    const key = `${tag}${index}`;

    if (isEditableTag(index)) {
      return (
        <EditManualField
          key={key}
          manualChoice={manualChoice}
          jsonObject={jsonObject}
          currentValue={
            manualChoiceItem.value ? { label: tag, value: manualChoiceItem.value } : undefined
          }
          onComplete={(value) => onComplete(index, value)}
        />
      );
    }
    // Create a display tag that shows the jsonPath when tag is empty
    const displayTag = tag.trim() || manualChoiceItem.value || '-';
    const isLongTag = displayTag.length > 20;

    const tagElem = (
      <Tooltip placement="topLeft" title={'Double click to change'} key={key}>
        <Tag className={styles.editTag} key={key} closable onClose={() => handleClose(index)}>
          <span
            onDoubleClick={() => {
              handleEdit(index);
            }}
          >
            {isLongTag ? `${displayTag.slice(0, 20)}...` : displayTag}
          </span>
        </Tag>
      </Tooltip>
    );
    return isLongTag ? (
      <Tooltip title={displayTag} key={key} placement="bottom">
        {tagElem}
      </Tooltip>
    ) : (
      tagElem
    );
  });

  return (
    <>
      <Row gutter={[32, 32]} style={{ textAlign: 'left' }}>
        <Col span={24}>
          {choicesTags}
          {isEditableTag(choicesTags.length) ? (
            <EditManualField
              onlyPrefix={onlyPrefix}
              jsonObject={jsonObject}
              manualChoice={manualChoice}
              onComplete={(value) => onComplete(choicesTags.length, value)}
            />
          ) : (
            <Tooltip placement="topLeft" title={'Click to add extra data to this field'}>
              <Tag
                color={classnames(isRequired ? 'red' : 'green')}
                onClick={() => handleEdit(choicesTags.length)}
              >
                <PlusOutlined /> Add data
              </Tag>
            </Tooltip>
          )}
        </Col>
        {manualChoice.infoText && (
          <Col>
            <Alert
              style={{ padding: 16, margin: 16 }}
              message={manualChoice.infoText}
              type="info"
              showIcon
            />
          </Col>
        )}
        <Col>
          <Button type="primary" disabled={deepEqual(savedPreference, preference)} onClick={onSave}>
            Save changes
          </Button>
        </Col>
        <Col>
          <Button
            disabled={deepEqual(savedPreference, currentChoices)}
            type="ghost"
            onClick={onCancel}
          >
            Cancel change
          </Button>
        </Col>
        {manualChoice.default && (
          <Col>
            <Button type="ghost" onClick={onReset}>
              Pick default
            </Button>
          </Col>
        )}
        <Col>
          <Button type="ghost" onClick={onClear}>
            Clear
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default ConfigureManualField;
